import { useEffect } from 'react';
import type { RefObject } from 'react';

/**
 * Lo que hace usable una capa que tapa la pantalla.
 *
 * Estaba dentro de `Modal` y no era de `Modal`: son las cuatro cosas que
 * cualquier sobrecapa tiene que hacer y que nadie ve hasta que faltan —el foco
 * entra al abrirse y vuelve al disparador al cerrarse, el fondo deja de hacer
 * scroll, y `Escape` cierra—. El visor de ejecución a pantalla completa
 * necesitaba exactamente las mismas cuatro, así que vive aquí en lugar de
 * escribirse por segunda vez con tres de ellas.
 *
 * El primer elemento enfocable, o el propio panel si no hay ninguno: abrir una
 * capa y dejar el foco fuera de ella es lo que hace que el teclado siga
 * navegando la página de detrás. Los elementos marcados con `data-dismiss` no
 * cuentan — el foco inicial no debe caer en el botón de cerrar.
 */
export function useOverlayChrome(open: boolean, onClose: () => void, panel: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    if (!open) {
      return;
    }

    const restoreTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const focusable = panel.current?.querySelector<HTMLElement>(
      'input, textarea, select, button:not([data-dismiss]), [href], [tabindex]:not([tabindex="-1"])',
    );

    (focusable ?? panel.current)?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      restoreTo?.focus();
    };
  }, [open, onClose, panel]);
}
